import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { readSelectedStaffId, writeSelectedStaffId } from "./staffIdentityStorage";

interface StaffIdentityContextType {
  /** The staff member this device is signed in as, or null if nobody has picked yet. */
  selectedStaffId: number | null;
  selectStaff: (staffId: number | null) => void;
}

const StaffIdentityContext = createContext<StaffIdentityContextType | undefined>(undefined);

/**
 * Who is at the keyboard, on a machine that several people share.
 *
 * A staff member does not necessarily have an email — plenty of reception teams have one
 * login for the desk — so the API cannot always work out whose tasks, calls and Handover
 * messages to show from the token alone. This holds the answer they picked.
 *
 * Deliberately NOT an authentication mechanism. The selection rides along as a header and
 * the API only honours it when the login's own email matches no staff profile; a personal
 * login is always itself, whatever is picked here. Treat this as "whose work am I looking
 * at", never as "what am I allowed to do".
 */
export const StaffIdentityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedStaffId, setSelectedStaffId] = useState<number | null>(() => readSelectedStaffId());

  const selectStaff = useCallback((staffId: number | null) => {
    // Storage first: it is what ClubBookingApiClient reads when it stamps the header, so
    // any request fired by the re-render below must already see the new value.
    writeSelectedStaffId(staffId);
    setSelectedStaffId(staffId);
  }, []);

  const value = useMemo(() => ({ selectedStaffId, selectStaff }), [selectedStaffId, selectStaff]);

  return <StaffIdentityContext.Provider value={value}>{children}</StaffIdentityContext.Provider>;
};

export const useStaffIdentity = (): StaffIdentityContextType => {
  const context = useContext(StaffIdentityContext);
  if (!context) throw new Error("useStaffIdentity must be used within a StaffIdentityProvider");
  return context;
};
